// dashboard/index.js

import React, { useState } from "react";
import axios from "../../api";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import GroupsIcon from "@mui/icons-material/Groups";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";

import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  DialogContent,
  Dialog,
  DialogTitle,
  Button,
  Chip,
  TextField,
  DialogActions,
} from "@mui/material";
import {
  Bar,
  XAxis,
  YAxis,
  BarChart,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { BoxIcon } from "lucide-react";
import {
  Add,
  CancelOutlined,
  CheckBoxOutlined,
  PendingActionsTwoTone,
  ProductionQuantityLimits,
  ShoppingCart,
  Visibility,
} from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { RootState } from "store";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// ---------- INTERFACES ----------

interface Stock {
  name: string;
  stock: number;
}

interface SpareData {
  spare_part_name: string;
  custom_spare_name: string;
  spare_part_id: number;
  quantity: number;
  asset_type_id: number;
  request_id: number;
}

interface Request {
  request_id: number | string;
}

// ---------- EXTRACTED COMPONENTS ----------

interface SparePartCellProps {
  row: SpareData;
}

const SparePartCell: React.FC<SparePartCellProps> = ({ row }) => {
  if (row.spare_part_name) {
    return <>{row.spare_part_name}</>;
  }

  if (row.custom_spare_name) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <span>{row.custom_spare_name}</span>
        <Chip
          label="New"
          size="small"
          color="primary"
          sx={{
            fontSize: "0.7rem",
            fontWeight: "bold",
            height: 20,
            borderRadius: "6px",
          }}
        />
      </Box>
    );
  }

  return <>-</>;
};

interface ActionsCellProps {
  row: SpareData;
  onApprove: (req: SpareData) => void;
  onReject: (requestId: number) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  row,
  onApprove,
  onReject,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box
      sx={{
        position: "relative",
        "&:hover .approve-text": { opacity: 1, width: "auto" },
      }}
    >
      <Button
        variant="contained"
        startIcon={<CheckBoxOutlined />}
        sx={{
          textTransform: "none",
          fontWeight: "bold",
          transition: "all 0.3s ease",
        }}
        onClick={() => onApprove(row)}
      >
        <Box
          className="approve-text"
          sx={{
            opacity: 0,
            width: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
            transition: "all 0.3s ease",
          }}
        >
          Approve
        </Box>
      </Button>
    </Box>

    <Box
      sx={{
        position: "relative",
        "&:hover .reject-text": { opacity: 1, width: "auto" },
      }}
    >
      <Button
        variant="contained"
        color="error"
        startIcon={<CancelOutlined />}
        sx={{
          ml: 1,
          textTransform: "none",
          fontWeight: "bold",
          transition: "all 0.3s ease",
        }}
        onClick={() => onReject(row.request_id)}
      >
        <Box
          className="reject-text"
          sx={{
            opacity: 0,
            width: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
            transition: "all 0.3s ease",
          }}
        >
          Reject
        </Box>
      </Button>
    </Box>
  </Box>
);

// ---------- HELPER FUNCTIONS ----------

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

// Validation Schema
const validationSchema = Yup.object({
  spare_part_name: Yup.string().required("Spare part name is required"),
  quantity: Yup.number()
    .min(1, "Quantity must be at least 1")
    .required("Quantity is required"),
  price: Yup.number()
    .min(0, "Price must be positive")
    .required("Price is required"),
  sku: Yup.string().required("SKU is required"),
});

const StockManagerDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { fetchError, fetchSuccess } = useSnackbar();
  const [count, setCount] = React.useState([]);
  const [orderCount, setOrderCount] = React.useState([]);
  const [pendingCount, setPendingCount] = React.useState([]);
  const [deliveredCount, setDeliveredCount] = React.useState([]);
  const [cancelledCount, setCancelledCount] = React.useState([]);
  const [stockTrends, setStockTrends] = useState<Stock[]>([]);
  const [rows, setRows] = React.useState<SpareData[]>([]);
  const [openModal, setOpenModal] = React.useState(false);
  const [selectedRequest, setSelectedRequest] = React.useState<Request | null>(
    null
  );

  const [initialValues, setInitialValues] = React.useState({
    spare_part_name: "",
    quantity: 1,
    price: 0,
    sku: "",
    asset_type_id: 0,
  });

  const cardStyle = {
    height: "100%",
    boxShadow: 3,
    borderRadius: 2,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      boxShadow: 5,
      transform: "scale(1.05)",
    },
  };

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get("/spare-parts");
      const data = response?.data || [];
      const monthData = new Array(12).fill(0);

      // Using for...of instead of forEach
      for (const item of data as Array<{
        created_at: string | Date;
        quantity: number;
      }>) {
        if (item.created_at) {
          const month = new Date(item.created_at).getMonth();
          monthData[month] += item.quantity || 0;
        }
      }

      const formatted = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ].map((m, i) => ({
        name: m,
        stock: monthData[i],
      }));
      setStockTrends(formatted);
      setCount(response?.data?.length);
    } catch (error) {
      console.error(error);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchOrders = React.useCallback(async () => {
    try {
      const response = await axios.get("/spare-order");
      const orders = response?.data || [];
      const pending = orders.filter(
        (o: { order_status: string }) => o.order_status === "Pending"
      ).length;
      const delivered = orders.filter(
        (o: { order_status: string }) => o.order_status === "Delivered"
      ).length;
      const cancelled = orders.filter(
        (o: { order_status: string }) => o.order_status === "Cancelled"
      ).length;
      setOrderCount(orders?.length);
      setPendingCount(pending);
      setDeliveredCount(delivered);
      setCancelledCount(cancelled);
    } catch (error) {
      console.error(error);
    }
  }, []);

  React.useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleStatusUpdate = async (id: number, status: string) => {
    try {
      await axios.put(`/spare-part-request/${id}/status`, {
        status,
        approved_by: user?.userId,
        remarks:
          status === "Approved" ? "Approved by admin" : "Rejected by admin",
      });
      fetchSuccess(`Request ${status} successfully`);
      fetchRequests();
    } catch (err) {
      if (err instanceof AxiosError && err.response) {
        fetchError(err.response?.data?.message || "Failed to update status");
      }
    }
  };

  const handleApprove = (req: SpareData) => {
    if (req.custom_spare_name && !req.spare_part_id) {
      setSelectedRequest(req);
      setInitialValues({
        spare_part_name: req.custom_spare_name || "",
        quantity: req.quantity || 1,
        price: 0,
        sku: "",
        asset_type_id: Number(req.asset_type_id || 0),
      });
      setOpenModal(true);
    } else {
      handleStatusUpdate(req.request_id, "Approved");
    }
  };

  const handleReject = (requestId: number) => {
    handleStatusUpdate(requestId, "Rejected");
  };

  const renderDateCell = (value: string | Date) => {
    if (!value) return "-";
    return dayjs(value).format("MMM DD, YYYY");
  };

  const renderSparePartCell = (dataIndex: number) => {
    const row = rows[dataIndex];
    return <SparePartCell row={row} />;
  };

  const renderActionsCell = (dataIndex: number) => {
    const row = rows[dataIndex];
    return (
      <ActionsCell
        row={row}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    );
  };

  const columns: MUIDataTableColumn[] = [
    { name: "request_id", label: "Request Id" },
    { name: "asset_type_name", label: "Asset Type" },
    {
      name: "spare_part_name",
      label: "Spare Part",
      options: {
        customBodyRenderLite: renderSparePartCell,
      },
    },
    { name: "requested_name", label: "Requested By" },
    { name: "quantity", label: "Quantity" },
    {
      name: "request_date",
      label: "Request Date",
      options: {
        customBodyRender: renderDateCell,
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: renderActionsCell,
      },
    },
  ];

  const fetchRequests = React.useCallback(async () => {
    try {
      const response = await axios.get("/spare-part-request/pending");
      setRows(response?.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  React.useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSubmit = async (
    values: {
      spare_part_name: string;
      price: number;
      quantity: number;
      asset_type_id: number;
      sku: string;
    },
    { setSubmitting }: any
  ) => {
    if (!selectedRequest) return;
    try {
      await axios.post(
        `/spare-part-request/${selectedRequest?.request_id}/add-part`,
        {
          spare_part_name: values.spare_part_name,
          price: Number(values.price),
          quantity: Number(values.quantity),
          asset_type_id: Number(values.asset_type_id),
          sku: values.sku,
          client_id: user?.clientId,
        }
      );
      fetchSuccess("Spare part created and linked successfully");
      await handleStatusUpdate(Number(selectedRequest.request_id), "Approved");
      setOpenModal(false);
      fetchRequests();
    } catch (err) {
      if (err instanceof AxiosError && err.response) {
        fetchError(
          err.response?.data?.message || "Failed to create spare part"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3}>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2.4}>
          <Card
            sx={{
              ...cardStyle,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 6,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Total Products
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  <strong>{count}</strong>
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "10%",
                  background: "#e1f2f2",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <BoxIcon color="black" size={28} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card
            sx={{
              ...cardStyle,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 6,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Total Orders
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  <strong>{orderCount}</strong>
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "10%",
                  background: "#e9e0fd",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <ShoppingCart sx={{ color: "white", fontSize: 28 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card
            sx={{
              ...cardStyle,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 6,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Pending Orders
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  <strong>{pendingCount}</strong>
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "10%",
                  background: "#fdf5e1",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <PendingActionsTwoTone sx={{ color: "white", fontSize: 28 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card
            sx={{
              ...cardStyle,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 6,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Delivered Orders
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  <strong>{deliveredCount}</strong>
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "10%",
                  background: "#e1f2f2",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <GroupsIcon sx={{ color: "white", fontSize: 28 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card
            sx={{
              ...cardStyle,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 6,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Cancelled Orders
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  <strong>{cancelledCount}</strong>
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "10%",
                  background: "#fceddd",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <ProductionQuantityLimits
                  sx={{ color: "white", fontSize: 28 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Products and Stock Movement */}
      <Grid container spacing={3} mb={5}>
        {/* Stock Trends - Left */}
        <Grid item xs={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Stock Trends
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stockTrends}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="stock" fill="#42A5F5" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side Stats / Insights */}
        <Grid item xs={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                {/* Add Spare Parts Card */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      p: 2,
                      mb: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: 6,
                        transform: "scale(1.05)",
                        transition: "all 0.2s ease-in-out",
                      },
                    }}
                    onClick={() => navigate("/spare-parts-list")}
                  >
                    <Typography variant="body2" mb={1}>
                      Add Stock
                    </Typography>
                    <Add sx={{ color: "var(--primary-color, #2e7d32)", fontSize: 32 }} />
                  </Card>
                </Grid>

                {/* View Orders Card */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      p: 2,
                      mb: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: 6,
                        transform: "scale(1.05)",
                        transition: "all 0.2s ease-in-out",
                      },
                    }}
                    onClick={() => navigate("/orders-list")}
                  >
                    <Typography variant="body2" mb={1}>
                      View Orders
                    </Typography>
                    <Visibility sx={{ color: "var(--primary-color, #2e7d32)", fontSize: 32 }} />
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid item xs={12} md={8}>
        <ThemeProvider theme={getMuiTheme}>
          <MUIDataTable
            title={"Recent Orders"}
            data={rows}
            columns={columns}
            options={tableOptions}
          />
        </ThemeProvider>
      </Grid>
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <SoftTypography variant="h5" fontWeight="bold">
            Create New Spare Part
          </SoftTypography>
        </DialogTitle>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <DialogContent>
                <SoftBox>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <SoftBox mb={1}>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Spare Part Name{" "}
                          <span style={{ color: "red" }}>*</span>
                        </SoftTypography>
                      </SoftBox>
                      <Field
                        name="spare_part_name"
                        as={TextField}
                        fullWidth
                        variant="outlined"
                        placeholder="Enter spare part name"
                        error={
                          !!errors.spare_part_name && touched.spare_part_name
                        }
                        helperText={
                          touched.spare_part_name && errors.spare_part_name
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SoftBox mb={1}>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Quantity <span style={{ color: "red" }}>*</span>
                        </SoftTypography>
                      </SoftBox>
                      <Field
                        name="quantity"
                        as={TextField}
                        type="number"
                        fullWidth
                        variant="outlined"
                        placeholder="Enter quantity"
                        error={!!errors.quantity && touched.quantity}
                        helperText={touched.quantity && errors.quantity}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SoftBox mb={1}>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Price <span style={{ color: "red" }}>*</span>
                        </SoftTypography>
                      </SoftBox>
                      <Field
                        name="price"
                        as={TextField}
                        type="number"
                        fullWidth
                        variant="outlined"
                        placeholder="Enter price"
                        error={!!errors.price && touched.price}
                        helperText={touched.price && errors.price}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SoftBox mb={1}>
                        <SoftTypography variant="caption" fontWeight="bold">
                          SKU <span style={{ color: "red" }}>*</span>
                        </SoftTypography>
                      </SoftBox>
                      <Field
                        name="sku"
                        as={TextField}
                        fullWidth
                        variant="outlined"
                        placeholder="Enter SKU"
                        error={!!errors.sku && touched.sku}
                        helperText={touched.sku && errors.sku}
                      />
                    </Grid>
                  </Grid>
                </SoftBox>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                  onClick={() => setOpenModal(false)}
                  variant="outlined"
                  color="secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save & Approve"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default StockManagerDashboard;
